import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Brain, Play, RotateCcw, ChevronRight, Check, X, Trophy,
  BarChart3, Trash2, Clock, Target, Filter
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { API } from "@/config/constants";

const SERVICE_OPTIONS = [
  { value: "all", label: "All Services" },
  { value: "ground", label: "PCP/ACP-G" },
  { value: "sfa_emr", label: "SFA/EMR" },
  { value: "wes", label: "PCP-PS" },
  { value: "air_adult", label: "Air Ambulance – Adult" },
  { value: "air_pediatric", label: "Air Ambulance – OB/NEO/PEDS" },
];

const QUIZ_SIZES = [5, 10, 15, 20];

function QuizSetup({ onStart }) {
  const [service, setService] = useState("all");
  const [count, setCount] = useState(10);

  return (
    <div className="max-w-lg mx-auto space-y-6">
      <div className="text-center space-y-2">
        <div className="w-16 h-16 bg-blue-600/10 rounded-2xl flex items-center justify-center mx-auto">
          <Brain className="w-8 h-8 text-blue-500" />
        </div>
        <h2 className="text-2xl font-bold text-zinc-100">Quiz Mode</h2>
        <p className="text-zinc-400 text-sm">
          Test your knowledge on drug dosages, contraindications, and protocol procedures.
        </p>
      </div>

      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="pt-6 space-y-5">
          <div>
            <label className="text-sm font-medium text-zinc-300 mb-2 flex items-center gap-2">
              <Filter className="w-4 h-4" /> Service Filter
            </label>
            <select
              data-testid="quiz-service-select"
              value={service}
              onChange={(e) => setService(e.target.value)}
              className="w-full bg-zinc-800 border border-zinc-700 text-zinc-100 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {SERVICE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-sm font-medium text-zinc-300 mb-2 flex items-center gap-2">
              <Target className="w-4 h-4" /> Number of Questions
            </label>
            <div className="flex gap-2">
              {QUIZ_SIZES.map((size) => (
                <button
                  key={size}
                  data-testid={`quiz-size-${size}`}
                  onClick={() => setCount(size)}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
                    count === size
                      ? "bg-blue-600 text-white"
                      : "bg-zinc-800 text-zinc-400 hover:bg-zinc-750 hover:text-zinc-200"
                  }`}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>

          <Button
            data-testid="quiz-start-btn"
            onClick={() => onStart(service, count)}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3"
          >
            <Play className="w-4 h-4 mr-2" /> Start Quiz
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

function QuizQuestion({ question, index, total, onAnswer, answered, selectedIndex }) {
  const isCorrect = selectedIndex === question.correct_index;

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <div className="flex items-center justify-between text-sm text-zinc-400">
        <span>Question {index + 1} of {total}</span>
        <Badge variant="outline" className={`text-xs ${
          question.type === "dosage" ? "border-amber-500/50 text-amber-400" : "border-blue-500/50 text-blue-400"
        }`}>
          {question.type === "dosage" ? "Dosage Recall" : "Protocol Knowledge"}
        </Badge>
      </div>

      <div className="w-full bg-zinc-800 rounded-full h-1.5">
        <div
          className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
          style={{ width: `${((index + 1) / total) * 100}%` }}
        />
      </div>

      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="pt-6">
          <h3 className="text-lg font-semibold text-zinc-100 mb-5 leading-relaxed">
            {question.question}
          </h3>
          <div className="space-y-3">
            {question.options.map((option, i) => {
              let style = "bg-zinc-800 border-zinc-700 text-zinc-200 hover:bg-zinc-750 hover:border-zinc-600";
              if (answered) {
                if (i === question.correct_index) {
                  style = "bg-emerald-900/40 border-emerald-500 text-emerald-300";
                } else if (i === selectedIndex && !isCorrect) {
                  style = "bg-red-900/40 border-red-500 text-red-300";
                } else {
                  style = "bg-zinc-800/50 border-zinc-700/50 text-zinc-500";
                }
              }

              return (
                <button
                  key={i}
                  data-testid={`quiz-option-${i}`}
                  onClick={() => !answered && onAnswer(i)}
                  disabled={answered}
                  className={`w-full text-left px-4 py-3 rounded-lg border transition-all flex items-center gap-3 ${style}`}
                >
                  <span className="w-7 h-7 rounded-full bg-zinc-700/50 flex items-center justify-center text-xs font-bold shrink-0">
                    {String.fromCharCode(65 + i)}
                  </span>
                  <span className="text-sm">{option}</span>
                  {answered && i === question.correct_index && (
                    <Check className="w-5 h-5 ml-auto text-emerald-400 shrink-0" />
                  )}
                  {answered && i === selectedIndex && !isCorrect && (
                    <X className="w-5 h-5 ml-auto text-red-400 shrink-0" />
                  )}
                </button>
              );
            })}
          </div>

          {answered && (
            <div className="mt-4 p-3 bg-zinc-800/50 rounded-lg border border-zinc-700/50">
              <p className="text-xs text-zinc-400">
                <span className="font-medium text-zinc-300">Source: </span>
                {question.explanation}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function QuizResults({ score, total, service, onRestart, onHome }) {
  const [track, setTrack] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const percentage = Math.round((score / total) * 100);
  const token = localStorage.getItem("ems_token");

  const getGrade = () => {
    if (percentage >= 90) return { label: "Excellent", color: "text-emerald-400", bg: "bg-emerald-900/30" };
    if (percentage >= 70) return { label: "Good", color: "text-blue-400", bg: "bg-blue-900/30" };
    if (percentage >= 50) return { label: "Needs Review", color: "text-amber-400", bg: "bg-amber-900/30" };
    return { label: "Study More", color: "text-red-400", bg: "bg-red-900/30" };
  };
  const grade = getGrade();

  const handleSubmit = async () => {
    try {
      await axios.post(`${API}/quiz/submit`, { score, total, service, track }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSubmitted(true);
    } catch (err) {
      console.error("Failed to submit quiz:", err);
    }
  };

  useEffect(() => { handleSubmit(); }, []);

  return (
    <div className="max-w-md mx-auto space-y-6 text-center">
      <div className={`w-20 h-20 ${grade.bg} rounded-full flex items-center justify-center mx-auto`}>
        <Trophy className={`w-10 h-10 ${grade.color}`} />
      </div>

      <div>
        <h2 className="text-3xl font-bold text-zinc-100">{score}/{total}</h2>
        <p className={`text-lg font-medium mt-1 ${grade.color}`}>{grade.label}</p>
        <p className="text-zinc-500 text-sm mt-1">{percentage}% correct</p>
      </div>

      <div className="w-full bg-zinc-800 rounded-full h-3">
        <div
          className={`h-3 rounded-full transition-all duration-1000 ${
            percentage >= 70 ? "bg-emerald-500" : percentage >= 50 ? "bg-amber-500" : "bg-red-500"
          }`}
          style={{ width: `${percentage}%` }}
        />
      </div>

      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="pt-5 pb-4">
          <label className="flex items-center justify-between cursor-pointer">
            <span className="text-sm text-zinc-300">Save this result to my history</span>
            <button
              data-testid="quiz-track-toggle"
              onClick={() => {
                setTrack(!track);
                setSubmitted(false);
              }}
              className={`relative w-11 h-6 rounded-full transition-colors ${
                track ? "bg-blue-600" : "bg-zinc-700"
              }`}
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                track ? "translate-x-5" : ""
              }`} />
            </button>
          </label>
          {track && !submitted && (
            <Button onClick={handleSubmit} size="sm" className="mt-3 w-full bg-blue-600 hover:bg-blue-700">
              Save Result
            </Button>
          )}
          {track && submitted && (
            <p className="text-xs text-emerald-400 mt-2">Result saved to your history</p>
          )}
        </CardContent>
      </Card>

      <div className="flex gap-3">
        <Button
          data-testid="quiz-restart-btn"
          onClick={onRestart}
          variant="outline"
          className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
        >
          <RotateCcw className="w-4 h-4 mr-2" /> Try Again
        </Button>
        <Button
          data-testid="quiz-home-btn"
          onClick={onHome}
          className="flex-1 bg-blue-600 hover:bg-blue-700"
        >
          New Quiz
        </Button>
      </div>
    </div>
  );
}

function QuizHistory() {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("ems_token");

  const fetchHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/quiz/history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHistory(res.data.history || []);
    } catch (err) {
      console.error("Failed to fetch history:", err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const clearHistory = async () => {
    try {
      await axios.delete(`${API}/quiz/history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHistory([]);
    } catch (err) {
      console.error("Failed to clear history:", err);
    }
  };

  const serviceLabel = (svc) => SERVICE_OPTIONS.find(s => s.value === svc)?.label || svc;

  if (loading) return null;
  if (!history.length) return null;

  return (
    <Card className="bg-zinc-900 border-zinc-800 mt-6">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium text-zinc-300 flex items-center gap-2">
            <BarChart3 className="w-4 h-4" /> Recent Quiz History
          </CardTitle>
          <Button
            data-testid="quiz-clear-history"
            variant="ghost"
            size="sm"
            onClick={clearHistory}
            className="text-zinc-500 hover:text-red-400 h-7 px-2"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-2">
          {history.slice(0, 10).map((entry, i) => (
            <div key={i} className="flex items-center justify-between py-2 px-3 bg-zinc-800/50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                  entry.percentage >= 70 ? "bg-emerald-900/40 text-emerald-400" : "bg-amber-900/40 text-amber-400"
                }`}>
                  {entry.percentage}%
                </div>
                <div>
                  <p className="text-sm text-zinc-200">{entry.score}/{entry.total}</p>
                  <p className="text-xs text-zinc-500">{serviceLabel(entry.service)}</p>
                </div>
              </div>
              <div className="flex items-center gap-1 text-xs text-zinc-500">
                <Clock className="w-3 h-3" />
                {new Date(entry.timestamp).toLocaleDateString()}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default function QuizPage() {
  const [phase, setPhase] = useState("setup"); // setup, active, results
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [answered, setAnswered] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [service, setService] = useState("all");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const token = localStorage.getItem("ems_token");
  const navigate = useNavigate();

  const startQuiz = async (selectedService, count) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/quiz/generate`, {
        params: { service: selectedService, count },
        headers: { Authorization: `Bearer ${token}` },
      });
      setQuestions(res.data.questions);
      setService(selectedService);
      setCurrentIndex(0);
      setScore(0);
      setAnswered(false);
      setSelectedIndex(null);
      setPhase("active");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to generate quiz. Try a different service.");
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (optionIndex) => {
    setSelectedIndex(optionIndex);
    setAnswered(true);
    if (optionIndex === questions[currentIndex].correct_index) {
      setScore((s) => s + 1);
    }
  };

  const nextQuestion = () => {
    if (currentIndex + 1 >= questions.length) {
      setPhase("results");
    } else {
      setCurrentIndex((i) => i + 1);
      setAnswered(false);
      setSelectedIndex(null);
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto">
      {phase === "setup" && (
        <>
          <QuizSetup onStart={startQuiz} />
          {loading && (
            <div className="text-center mt-4">
              <div className="inline-block w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-zinc-400 mt-2">Generating questions...</p>
            </div>
          )}
          {error && (
            <div className="mt-4 p-3 bg-red-900/20 border border-red-800 rounded-lg text-center">
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}
          <QuizHistory />
        </>
      )}

      {phase === "active" && questions[currentIndex] && (
        <div className="space-y-4">
          <QuizQuestion
            question={questions[currentIndex]}
            index={currentIndex}
            total={questions.length}
            onAnswer={handleAnswer}
            answered={answered}
            selectedIndex={selectedIndex}
          />
          {answered && (
            <div className="max-w-2xl mx-auto">
              <Button
                data-testid="quiz-next-btn"
                onClick={nextQuestion}
                className="w-full bg-blue-600 hover:bg-blue-700 py-3"
              >
                {currentIndex + 1 >= questions.length ? "See Results" : "Next Question"}
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}
        </div>
      )}

      {phase === "results" && (
        <QuizResults
          score={score}
          total={questions.length}
          service={service}
          onRestart={() => startQuiz(service, questions.length)}
          onHome={() => setPhase("setup")}
        />
      )}
    </div>
  );
}
